import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  PLATFORM_ID,
  TransferState,
  afterNextRender,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { Observable, catchError, of, timeout } from 'rxjs';

import { environment } from '@environment/environment';

import { provideApi, httpForwardedForInterceptor } from '@features/api';
import { provideLocations } from '@features/locations';
import { AuthService, httpTokenInterceptor, JWTService, User } from '@features/auth';
import { httpDeviceInterceptor } from '@features/device';
import { httpErrorInterceptor } from '@features/errors';
import { provideRyft } from '@features/ryft';
import { provideAnalytics } from '@features/analytics';

import { routes } from './app.routes';
import { runRedirectShim } from '../server/redirect.shim';
import { isServerRenderedPage } from '../server/ssr-marker';

/**
 * How long the session initializer may block bootstrap before the app starts
 * anyway as an unauthenticated visitor. Matches `API_TIMEOUT_MS` in
 * `redirect.shim.ts` so the two bootstrap-blocking network waits agree.
 */
const AUTH_INIT_TIMEOUT_MS = 8_000;

/**
 * ============================================================================
 *  APP INITIALIZER INVARIANT — READ BEFORE ADDING ONE (BBS-0070)
 * ============================================================================
 *
 * Angular only bootstraps the root component AFTER every blocking app
 * initializer has settled. In `ApplicationInitStatus`, `appRef.bootstrap()` sits
 * inside `donePromise.then(...)` — so a returned promise/observable that
 * REJECTS, or that never settles, means the root component is never created and
 * hydration never runs.
 *
 * On an SSR build that failure is close to invisible and very expensive: the
 * server-rendered HTML is already on screen and looks perfect, `<a href>` links
 * still navigate, but not one `(click)` handler is ever attached. The whole site
 * silently loses its buttons, menus, dropdowns and checkout. This exact
 * situation shipped to production: a blocked Google Tag Manager script rejected
 * the analytics initializer and took every interactive element with it.
 *
 * THE RULE: an app initializer may NEVER reject and may NEVER hang.
 *
 * Concretely, anything returned from `provideAppInitializer` must:
 *   1. catch its own errors and resolve/complete anyway;
 *   2. be bounded in time (HttpClient and `fetch` have NO default timeout);
 *   3. not read anything that can throw synchronously — `localStorage` throws on
 *      property access under Safari's "Block All Cookies" (see `SafeStorage`);
 *   4. return nothing at all when the work is genuinely optional, i.e. do it
 *      fire-and-forget rather than making bootstrap wait on it.
 *
 * Third-party SDKs are the usual offender: assume any of them may be blocked by
 * a content blocker, a DNS filter or a corporate proxy. "It worked locally" is
 * not evidence — see `e2e/bootstrap-resilience.spec.ts`, which blocks each
 * dependency in turn and asserts the site stays interactive.
 *
 * AUDIT — every registered initializer, and how each satisfies the rule.
 * Re-run this audit when you add one, and record the result here.
 *
 *   1. initializeRedirectShim (below)  skipped on SSR; internal try/catch; every
 *                                     fetch has AbortSignal.timeout; the 6 MB
 *                                     bulk map is no longer awaited.
 *   2. initializeSession (below)       catchError → completes; timeout() caps the
 *                                     wait; reads only non-throwing JWTService.
 *   3. loadGtmAtBootstrap              returns void — fire-and-forget, so neither
 *      (via provideAnalytics)          the rejection nor the hang path exists.
 *   4. startPageViewTracking           returns void; synchronous; wrapped in a
 *      (via provideAnalytics)          try/catch.
 *   5. SSR marker                      server-side only, synchronous, one
 *      (app.config.server.ts)          TransferState write.
 *
 * Note that `provideAnalytics()` registers TWO initializers, so "the four in
 * app.config.ts" is five once the server config is merged. Count them in the
 * providers, not in the file.
 *
 * Each of the five is covered by a test that fails if the rule is broken:
 * `app.config.spec.ts` bootstraps a real `ApplicationInitStatus` with the
 * shipped initializers and a failing dependency, and
 * `e2e/bootstrap-resilience.spec.ts` blocks each dependency at the network layer
 * against a real SSR build.
 * ============================================================================
 */

/**
 * Client-side V1→V2 redirect layer for SPA deployments (no SSR / no redirect
 * server). Registered first, before routing, so old URLs redirect before the 404
 * renders. Browser-only; it is active on every SPA build and simply no-ops if
 * the map asset (/assets/redirects.json) is missing.
 *
 * Satisfies the invariant above: `runRedirectShim()` catches everything
 * internally, every fetch it makes carries an `AbortSignal.timeout`, and it no
 * longer waits on the ~6 MB bulk map. It is skipped outright on SSR builds,
 * where `createRedirectMiddleware` in `server.ts` has already applied redirects
 * server-side and this would only re-download that map for nothing.
 *
 * Exported, rather than inlined into the providers below, so the invariant can
 * actually be asserted against the shipped initializer instead of a copy of it —
 * see `app.config.spec.ts`.
 */
export function initializeRedirectShim(): Promise<void> | void {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
  if (isServerRenderedPage(inject(TransferState))) return;

  return runRedirectShim();
}

/**
 * Establishes the session (restore the stored token, or start a guest one).
 *
 * Blocking on purpose — components read `authenticated()` immediately — but it
 * can neither reject nor hang, per the invariant above. A failed or slow session
 * start leaves the visitor unauthenticated and fully interactive; the failure is
 * logged (endpoint + status only) inside `AuthService`.
 *
 * Exported for the same reason as `initializeRedirectShim`.
 */
export function initializeSession(): Observable<User | null> | void {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) return;

  const jwt = inject(JWTService);
  const auth = inject(AuthService);

  // Registered BEFORE the request so it is queued whatever the outcome below.
  // `authReady` must flip after hydration, not when the API answers, or the
  // masthead would render its logged-in state into server HTML that does not
  // contain it (NG0500).
  afterNextRender(() => auth.markReady());

  // `jwt.token` cannot throw (see JWTService), so this is safe to read here.
  return (jwt.token ? auth.populate() : auth.createGuest()).pipe(
    // HttpClient has no default timeout, so a request left hanging by a captive
    // portal or a dead API would stall bootstrap indefinitely — the same
    // dead-page symptom as a rejection.
    timeout({ each: AUTH_INIT_TIMEOUT_MS, with: () => of(null) }),
    catchError(() => of(null)),
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(initializeRedirectShim),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      withComponentInputBinding(),
      withViewTransitions(),
    ),
    provideApi(environment.api_url, {
      interceptors: [httpTokenInterceptor, httpDeviceInterceptor, httpForwardedForInterceptor, httpErrorInterceptor],
    }),
    { provide: LOCALE_ID, useValue: 'en-GB' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'GBP' },
    provideAppInitializer(initializeSession),
    // Registers TWO app initializers — `loadGtmAtBootstrap` and
    // `startPageViewTracking` (see `provide-analytics.ts`). Both return `void`, so
    // neither can reject or hang. The first of them is the original cause of
    // BBS-0070: it used to return `addGtmToDom()`'s promise, which let a blocked
    // Google Tag Manager script take every button on the site down with it.
    provideAnalytics({ id: environment.gtm.id }),
    provideLocations(),
    provideRyft({
      publicKey: environment.ryft.publicKey,
      googlePay: environment.ryft.googlePay,
      applePay: environment.ryft.applePay,
    }),
    provideAnimationsAsync(),
    provideClientHydration(withEventReplay()),
  ],
};
